import {
  PERSISTED_CONFIG_FIELD_PATHS,
  SAVED_TEST_FIELD_MAP,
} from "@constants/testConstants/testDefinitions";
import { CONFIGURATION_SELECTOR_UI_DATA_MAP } from "@constants/uiConstants";
import { cloneDeep, get, set } from "lodash";
import {
  AvailableOption,
  AvailablePreTestConfigurationOptions,
  AdjustableConfigurationValue,
  AllConfigurationKeys,
  TestConfiguration,
  PersistedConfiguration,
  TestCategory,
  TestLanguage,
} from "types/tests";

/**
 * Gets a list of choice values from an AvailableOption object.
 * @param optionsHolder The AvailableOption object containing choices.
 * @return An array of choice values or null if choices are not available.
 */
export function getChoiceValueListFromOption(
  optionsHolder: AvailableOption<any>,
): AdjustableConfigurationValue[] | null {
  if (!optionsHolder || !optionsHolder.choices || !Array.isArray(optionsHolder.choices))
    return null;
  return optionsHolder.choices.filter((choice) => !choice.disabled).map((choice) => choice.value);
}

export function getConfigurationNumberBounds(fieldName: AllConfigurationKeys | TestCategory): {
  minimum?: number;
  maximum?: number;
} {
  const metadata = CONFIGURATION_SELECTOR_UI_DATA_MAP[fieldName];
  return { minimum: metadata?.numberInputMinimum, maximum: metadata?.numberInputMaximum };
}

export function clampConfigurationNumber(
  value: number,
  fieldName: AllConfigurationKeys | TestCategory,
): number {
  const { minimum, maximum } = getConfigurationNumberBounds(fieldName);
  let clamped = value;
  if (typeof minimum === "number") {
    clamped = Math.max(clamped, minimum);
  }
  if (typeof maximum === "number") {
    clamped = Math.min(clamped, maximum);
  }
  return clamped;
}

export function isFreeFormNumberOption(option?: AvailableOption<any> | null): boolean {
  return option?.selectorType === "numberInput";
}

/**
 * Merges persisted configuration fields back into the sanitized configuration
 * Only applies values if the field is available in the new test's options
 */
export function mergePersistedFields(
  persistedConfig: PersistedConfiguration,
  sanitizedConfig: TestConfiguration,
  availableOptions: AvailablePreTestConfigurationOptions,
): TestConfiguration {
  const result = cloneDeep(sanitizedConfig);

  for (const path of PERSISTED_CONFIG_FIELD_PATHS) {
    const persistedValue = get(persistedConfig, path);

    if (persistedValue === undefined) {
      continue;
    }

    // Check if this field is available in the new test's options
    const isFieldAvailable = checkFieldAvailability(path, availableOptions);

    if (isFieldAvailable) {
      set(result, path, persistedValue);
    }
  }

  return result;
}

export function applyFirstLanguageToVirtualAssistant(
  configuration: TestConfiguration,
  firstLanguage: TestLanguage | undefined,
  availableOptions: AvailablePreTestConfigurationOptions | null,
): TestConfiguration {
  const assistantOptions = availableOptions?.virtualAssistantOptions;
  if (!firstLanguage || !assistantOptions || !configuration?.virtualAssistanceConfig) {
    return configuration;
  }
  if (!(getChoiceValueListFromOption(assistantOptions) ?? []).includes(firstLanguage)) {
    return configuration;
  }

  return {
    ...configuration,
    virtualAssistanceConfig: {
      ...configuration.virtualAssistanceConfig,
      assistantLanguage: firstLanguage,
    },
  };
}

/**
 * Checks if a configuration field path is available in the test's options
 */
function checkFieldAvailability(
  path: string,
  availableOptions: AvailablePreTestConfigurationOptions,
): boolean {
  // Map paths to their corresponding available options
  const pathToOptionMap: Record<string, keyof AvailablePreTestConfigurationOptions> = {
    "virtualAssistanceConfig.showAssistant": "virtualAssistantOptions",
    "virtualAssistanceConfig.quickAssistant": "virtualAssistantOptions",
    "virtualAssistanceConfig.assistantLanguage": "virtualAssistantOptions",
    "voiceAssistantConfig.isVoiceAssistantEnabled": "voiceAssistantOptions",
    "voiceAssistantConfig.assistantLanguage": "voiceAssistantOptions",
  };

  const optionKey = pathToOptionMap[path];
  return optionKey ? availableOptions[optionKey] != null : false;
}

export function extractSavedFields(
  config: Partial<TestConfiguration> | null | undefined,
  testName: TestCategory,
): Partial<TestConfiguration> {
  if (!config) return {};

  const fieldPaths = SAVED_TEST_FIELD_MAP[testName] || [];
  const extracted: any = {};

  for (const path of fieldPaths) {
    const value = get(config, path);
    if (value !== undefined) {
      set(extracted, path, value);
    }
  }

  return extracted;
}

function isAvailableOption(value: unknown): value is AvailableOption<any> {
  return (
    Boolean(value) &&
    typeof value === "object" &&
    "configurationFieldName" in value &&
    "validSinceVersion" in value
  );
}

export function hasLoadingMandatoryConfigurationOption(
  availableOptions: AvailablePreTestConfigurationOptions | null | undefined,
): boolean {
  if (!availableOptions) {
    return false;
  }

  const visit = (value: unknown): boolean => {
    if (!value || typeof value !== "object") {
      return false;
    }

    if (isAvailableOption(value)) {
      return Boolean(value.isMandatory && value.isLoading);
    }

    return Object.values(value).some(visit);
  };

  return visit(availableOptions);
}
